import { TechnicianScheduleResponse } from 'services/technicianSchedule/types'
import { checkScheduleOverlap } from 'components/SOCalendarModal/utils'
import { ScheduleType } from 'components/SOCalendarModal/types'
import { convertDateToString } from 'utilities/datepicker'
import { WeekInterval } from 'components/Calendar/types'
import { maskedInputDate } from 'utilities/date'
import { EventInput } from '@fullcalendar/core'
import {
  ServiceOrderAggregatedQueryResponse,
  SERVICE_ORDER_STATUS_ENUM,
} from 'services/serviceOrder'
import {
  ServiceOrderAggregatedResponse,
  CalendarWeekInterval,
} from 'services/serviceOrderV2'

const isEventStartingWithinInterval = (
  event: EventInput,
  interval: WeekInterval,
): boolean => {
  const eventStartTime = event.start?.getTime()
  return (
    eventStartTime >= interval.startDate && eventStartTime <= interval.endDate
  )
}

const isEventEndingWithinInterval = (
  event: EventInput,
  interval: WeekInterval,
): boolean => {
  const eventEndTime = new Date(event.end).getTime()
  return eventEndTime >= interval.startDate && eventEndTime <= interval.endDate
}

const isEventSpanningEntireInterval = (
  event: EventInput,
  interval: WeekInterval,
): boolean => {
  const eventStartTime = event.start?.getTime()
  const eventEndTime = new Date(event.end).getTime()
  return (
    eventStartTime <= interval.startDate && eventEndTime >= interval.endDate
  )
}

export const filterSchedules = (
  events: EventInput[],
  interval?: WeekInterval,
): EventInput[] => {
  if (!interval) return []

  return (
    events?.filter(
      (event) =>
        isEventStartingWithinInterval(event, interval) ||
        isEventEndingWithinInterval(event, interval) ||
        isEventSpanningEntireInterval(event, interval),
    ) || []
  )
}

export const buildServiceOrderEvent = (
  data?: ServiceOrderAggregatedQueryResponse[],
) =>
  data?.map((serviceOrder: ServiceOrderAggregatedQueryResponse) => ({
    id: serviceOrder?.id,
    title: serviceOrder.customer?.name || '',
    tags: serviceOrder.tags,
    allDay: !!serviceOrder?.schedulePeriod,
    start: new Date(serviceOrder?.scheduleDate),
    end:
      !serviceOrder?.schedulePeriod && serviceOrder?.scheduleDate
        ? new Date(serviceOrder?.scheduleDate).setHours(
            new Date(serviceOrder?.scheduleDate).getHours() + 2,
          )
        : '',
    extendedProps: {
      schedulePeriod: serviceOrder?.schedulePeriod ?? undefined,
      status: serviceOrder?.status,
      number: serviceOrder?.number,
      accountCode: serviceOrder?.account?.code,
      customer: serviceOrder?.customer?.name,
      type: serviceOrder?.type,
    },
  })) || []

export const buildScheduleBlockEvents = (data?: TechnicianScheduleResponse[]) =>
  data?.map((scheduleBlock: TechnicianScheduleResponse) => ({
    id: scheduleBlock?.id,
    title: '',
    tags: '',
    allDay: false,
    start: new Date(scheduleBlock.startDate),
    end: new Date(scheduleBlock.endDate),
    extendedProps: {
      schedulePeriod: undefined,
      status: undefined,
      number: undefined,
      accountCode: '',
      customer: '',
      type: undefined,
    },
  })) || []

export const initialWeekInterval = {
  startDate: (() => {
    const now = new Date()
    const dayOfWeek = now.getDay()
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
    const monday = new Date(now.setDate(now.getDate() + diffToMonday))
    monday.setHours(0, 0, 0, 0)
    return monday.getTime()
  })(),
  endDate: (() => {
    const now = new Date()
    const dayOfWeek = now.getDay()
    const diffToSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek
    const sunday = new Date(now.setDate(now.getDate() + diffToSunday))
    sunday.setHours(0, 0, 0, 0)
    return sunday.getTime()
  })(),
}

const getScheduleType = (schedulePeriod?: string, id?: string): string => {
  if (id) {
    switch (schedulePeriod) {
      case 'BUSINESS_HOURS':
        return 'BUSINESS_HOURS'
      case 'DAY_SHIFT':
        return 'DAY_SHIFT'
      case 'MORNING':
        return 'DAY_SHIFT'
      case 'AFTERNOON':
        return 'DAY_SHIFT'
      default:
        return 'FIXED_SCHEDULE'
    }
  }

  return 'BUSINESS_HOURS'
}

const tomorrow = new Date().setDate(new Date().getDate() + 1)

export const buildSOSchedule = (
  serviceOrder?: ServiceOrderAggregatedResponse,
) => ({
  id: serviceOrder?.id,
  labels: serviceOrder?.labels,
  note: serviceOrder?.note || '',
  number: serviceOrder?.number || 0,
  technician: {
    id: serviceOrder?.technician?.id,
    name: serviceOrder?.technician?.name,
  },
  customer: {
    id: serviceOrder?.customer?.id,
    name: serviceOrder?.customer?.name,
  },
  status: serviceOrder?.status ?? 'OPEN',
  calendarWeekInterval: initialWeekInterval,
  serviceOrderType: serviceOrder?.serviceOrderType,
  schedulePeriod:
    serviceOrder?.id && serviceOrder.status !== 'OPEN'
      ? serviceOrder?.schedulePeriod ?? null
      : 'BUSINESS_HOURS',
  scheduleDate: serviceOrder?.scheduleDate ?? tomorrow,
  account: {
    id: serviceOrder?.account?.id,
    code: serviceOrder?.account?.code,
    aggregatedName: serviceOrder?.account?.aggregatedName,
  },
  scheduleType:
    serviceOrder?.status === 'OPEN'
      ? 'BUSINESS_HOURS'
      : getScheduleType(serviceOrder?.schedulePeriod, serviceOrder?.id),
  tags: serviceOrder?.tags?.map((tag) => ({ name: tag.name })) ?? [],
  dayShift:
    serviceOrder?.schedulePeriod === 'AFTERNOON' ? 'AFTERNOON' : 'MORNING',
  scheduleDateInput: serviceOrder?.scheduleDate
    ? maskedInputDate(convertDateToString(new Date(serviceOrder.scheduleDate)))
    : maskedInputDate(convertDateToString(new Date(tomorrow))),
  time:
    !serviceOrder?.schedulePeriod && serviceOrder?.scheduleDate
      ? `${new Date(serviceOrder?.scheduleDate).getHours()}:${String(new Date(serviceOrder?.scheduleDate).getMinutes()).padStart(2, '0')}`
      : '08:00',
})

export const buildGetSOFilters = (
  scheduleDate?: number,
  calendarWeekInterval?: WeekInterval,
  technicianId?: string,
) => ({
  ...(technicianId && { technicianIds: [technicianId] }),
  scheduleDateFrom:
    scheduleDate &&
    calendarWeekInterval?.startDate &&
    Math.min(scheduleDate, calendarWeekInterval.startDate),
  scheduleDateTo:
    scheduleDate &&
    calendarWeekInterval?.endDate &&
    Math.max(scheduleDate, calendarWeekInterval.endDate),
  status: [
    SERVICE_ORDER_STATUS_ENUM.SCHEDULED,
    SERVICE_ORDER_STATUS_ENUM.RESCHEDULED,
    SERVICE_ORDER_STATUS_ENUM.FINISHED,
    SERVICE_ORDER_STATUS_ENUM.IN_PROGRESS,
    SERVICE_ORDER_STATUS_ENUM.IN_VALIDATION,
    SERVICE_ORDER_STATUS_ENUM.REPROVED,
    SERVICE_ORDER_STATUS_ENUM.PAUSED,
    SERVICE_ORDER_STATUS_ENUM.OPEN,
  ],
})

export const validateTechnicianScheduleBlocked = (
  technicianSchedule?: TechnicianScheduleResponse[],
  scheduleDate?: Date,
  scheduleType?: ScheduleType,
  time?: string,
) => {
  if (technicianSchedule && scheduleDate) {
    const [hours, minutes] = time?.split(':')
    const chosenStartTime = scheduleDate
    if (time) {
      chosenStartTime.setHours(Number(hours))
      chosenStartTime.setMinutes(Number(minutes))
    }
    const chosenEndTime = chosenStartTime
    chosenEndTime.setHours(chosenEndTime.getHours() + 2)

    return technicianSchedule.some((block: TechnicianScheduleResponse) => {
      const blockStartDate = new Date(block.startDate)
      const blockEndDate = new Date(block.endDate)
      const isOverlap = checkScheduleOverlap(
        blockStartDate,
        blockEndDate,
        scheduleDate,
        scheduleType,
        chosenStartTime,
        chosenEndTime,
      )

      return isOverlap
    })
  }

  return false
}

export const isAnotherWeek = (
  calendarWeekInterval: CalendarWeekInterval,
  newInterval: WeekInterval,
) =>
  calendarWeekInterval?.startDate !== newInterval.startDate ||
  calendarWeekInterval?.endDate !== newInterval.endDate

export const createScheduledDate = (
  date?: number,
  hours?: string,
  minutes?: string,
) => {
  if (date) {
    const scheduleDate = new Date(date)
    scheduleDate?.setHours(Number(hours ?? 0))
    scheduleDate?.setMinutes(Number(minutes ?? 0))
    scheduleDate?.setSeconds(0)
    scheduleDate?.setMilliseconds(0)
    return scheduleDate.getTime()
  }
}

export const getHours = () => {
  const hours: { name: string }[] = []
  for (let hour = 8; hour <= 18; hour++) {
    const formattedHour = { name: hour.toString().padStart(2, '0') + ':00' }
    hours.push(formattedHour)
  }
  return hours
}
